// F1 App-Shell (TES-948): Desktop-SideNav (screen-04 / screen-02).
// Fixe w-sidebar-width (260px), bg-sidebar (#00234d). Aktiver Link:
// border-l-4 border-secondary (#fed400) + .active-nav-glow (inset 4px) + bg-sidebar-accent.
// Verlustfrei: 5 Rollen-Bloecke (via useNavSections), Offline-Guards, Badge-Counts
// + Pulse, GuestMode-Logik und ALLE data-tour-Attribute. Link-Rendering ausgelagert
// (SideNavItem / NavLinkBase / UserMenu) + Sektion/Guide-Link in Sub-Komponenten
// zerlegt zur Komplexitaets-Reduktion (Markup 1:1).
import { Link, useLocation } from "react-router";
import { Fragment } from "react";
import { ChevronLeft, ChevronRight, BookOpen } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { ActiveTeamSelector } from "@/components/team/ActiveTeamSelector";
import { SyncStatusIndicator } from "../SyncStatusIndicator";
import type { ShellModel } from "./useShellModel";
import { SideNavItem } from "./SideNavItem";
import { NavLinkBase } from "./NavLinkBase";
import { UserMenu } from "./UserMenu";
import { SideNavBrand } from "./SideNavBrand";
import { NavLabel } from "./navLinkParts";
import { hasOperativeRole, navDataTour } from "./navConfig";
import { useNavSections, navSectionKey, type NavSection } from "./useNavSections";

interface SideNavProps {
  model: ShellModel;
  collapsed: boolean;
  onToggleCollapsed: () => void;
}

const ACTIVE_LINK =
  "border-l-4 border-secondary active-nav-glow bg-sidebar-accent text-sidebar-accent-foreground";
const INACTIVE_LINK =
  "hover:bg-sidebar-accent text-sidebar-foreground/80 hover:text-sidebar-foreground";

// true, wenn das Section-Label gedimmt dargestellt wird (operativ/QM/admin im Offline).
function isLabelDimmed(section: NavSection, isOnline: boolean): boolean {
  if (!section.dimWhenOffline) return false;
  return !isOnline;
}

// Section-Label (nur expanded + benannt), dimmt optional bei Offline.
function SideNavSectionLabel({
  section,
  model,
  collapsed,
}: {
  section: NavSection;
  model: ShellModel;
  collapsed: boolean;
}) {
  const labelKey = section.labelKey;
  if (!labelKey || collapsed) return null;
  return (
    <div className={cn("pt-4 pb-2 px-3", isLabelDimmed(section, model.isOnline) && "opacity-40")}>
      <span className="text-xs font-semibold text-sidebar-foreground/70 uppercase tracking-wider">
        {model.t(labelKey)}
      </span>
    </div>
  );
}

// Eine Rollen-Sektion: Label + ihre Items (operativ via SideNavItem, sonst NavLinkBase).
function SideNavSection({
  section,
  model,
  collapsed,
}: {
  section: NavSection;
  model: ShellModel;
  collapsed: boolean;
}) {
  return (
    <>
      <SideNavSectionLabel section={section} model={model} collapsed={collapsed} />
      {section.items.map((item) =>
        section.isOperative ? (
          <SideNavItem key={item.nameKey} item={item} model={model} collapsed={collapsed} />
        ) : (
          <NavLinkBase
            key={item.nameKey}
            item={item}
            model={model}
            variant="desktop"
            collapsed={collapsed}
            // TES-1701: non-operative links (QM/Admin/Super/Platform) also carry their
            // GuideTour anchor so steps like „Teams" (data-tour="nav-teams") aren't
            // silently skipped. SideNavItem already wires this via navDataTour internally.
            dataTour={navDataTour(item.href)}
          />
        ),
      )}
    </>
  );
}

// collapsed-abhaengige Container-Klassen — statisch, aus dem Render-Body hochgezogen,
// damit die `collapsed ? a : b`-Ternaries nicht in SideNav zur Komplexitaet beitragen.
const ASIDE_WIDTH = { collapsed: "w-16", expanded: "w-sidebar-width" } as const;
const NAV_PAD = { collapsed: "p-2", expanded: "p-3" } as const;
const SYNC_PAD = { collapsed: "px-2 pb-1", expanded: "px-3 pb-1" } as const;
const USER_PAD = { collapsed: "p-2", expanded: "p-3" } as const;

function key(collapsed: boolean): "collapsed" | "expanded" {
  return collapsed ? "collapsed" : "expanded";
}

// Collapse-Toggle-Icon.
function ToggleIcon({ collapsed }: { collapsed: boolean }) {
  if (collapsed) return <ChevronRight className="h-4 w-4" />;
  return <ChevronLeft className="h-4 w-4" />;
}

const GUIDE_OUTER_PAD = { collapsed: "px-2", expanded: "px-3" } as const;
const GUIDE_LINK_PAD = { collapsed: "px-2 justify-center", expanded: "px-3" } as const;

// HILFE-Sektion mit Benutzeranleitung — eigene Sektion (Label „HILFE"), klar getrennt vom
// Admin-Block. Die Sichtbarkeit begrenzt der Aufrufer auf operative Rollen (TES-1683).
function SideNavGuideLink({ model, collapsed }: { model: ShellModel; collapsed: boolean }) {
  const location = useLocation();
  const k = key(collapsed);
  const linkClass = location.pathname === "/guide" ? ACTIVE_LINK : INACTIVE_LINK;
  const label = model.t("nav.userGuide");
  return (
    <>
      {!collapsed && (
        <div className="pt-4 pb-2 px-3">
          <span className="text-xs font-semibold text-sidebar-foreground/70 uppercase tracking-wider">
            {model.t("nav.help")}
          </span>
        </div>
      )}
      <div className={cn(collapsed && "pt-2", GUIDE_OUTER_PAD[k])}>
        <Link
          data-tour="help-button"
          to="/guide"
          aria-label={collapsed ? label : undefined}
          className={cn(
            "flex items-center gap-3 py-2.5 rounded-lg transition-all duration-200",
            GUIDE_LINK_PAD[k],
            linkClass,
          )}
        >
          <BookOpen className={cn("h-5 w-5 flex-shrink-0", collapsed && "mx-auto")} />
          <NavLabel hidden={collapsed}>{label}</NavLabel>
        </Link>
      </div>
    </>
  );
}

export function SideNav({ model, collapsed, onToggleCollapsed }: SideNavProps) {
  const { isSuperAdmin, isTenantContext, roles } = model;
  const sections = useNavSections(model);
  const k = key(collapsed);
  const showTeamSelector = !isSuperAdmin && !isTenantContext;
  // HILFE/Benutzeranleitung für operative Rollen, Super-Admin und rollenlose Fail-safe-Shells; nicht beim reinen Admin (TES-1683).
  const showUserGuide =
    !isTenantContext && (isSuperAdmin || roles.length === 0 || hasOperativeRole(roles));

  return (
    <aside
      className={cn(
        "hidden md:flex flex-col bg-sidebar text-sidebar-foreground transition-all duration-300",
        ASIDE_WIDTH[k],
      )}
    >
      {/* Logo + Collapse-Toggle */}
      <div className="flex h-16 items-center justify-between px-4 border-b border-sidebar-border">
        <SideNavBrand model={model} collapsed={collapsed} />
        <Button
          variant="ghost"
          size="icon"
          onClick={onToggleCollapsed}
          aria-label={model.t(collapsed ? "nav.expandSidebar" : "nav.collapseSidebar")}
          className="h-8 w-8 hover:bg-sidebar-accent"
        >
          <ToggleIcon collapsed={collapsed} />
        </Button>
      </div>

      {/* Active Team Selector - visible for multi-team users */}
      {showTeamSelector && <ActiveTeamSelector collapsed={collapsed} />}

      {/* Navigation */}
      <nav data-tour="sidebar-nav" className={cn("flex-1 space-y-1 overflow-y-auto", NAV_PAD[k])}>
        {/* TES-2061/TES-2008: mehrere Sektionen ohne labelKey (z.B. operative + qm_admin) —
            navSectionKey (geteilt mit NavDrawer) liefert Index-Fallback statt festem
            "__operative", sonst kollidieren die React-Keys (Duplikat-Rendering). */}
        {sections.map((section, idx) => (
          <Fragment key={navSectionKey(section, idx)}>
            <SideNavSection section={section} model={model} collapsed={collapsed} />
          </Fragment>
        ))}

        {showUserGuide && <SideNavGuideLink model={model} collapsed={collapsed} />}
      </nav>

      {/* Sync status indicator */}
      <div className={cn(SYNC_PAD[k])}>
        <SyncStatusIndicator collapsed={collapsed} />
      </div>

      {/* User section */}
      <div className={cn("border-t border-sidebar-border", USER_PAD[k])}>
        <UserMenu model={model} variant="sidebar" collapsed={collapsed} dataTour="user-menu" />
      </div>
    </aside>
  );
}
